import {
  computeVariant,
  CONTRACT_SIGN_FORMS,
  type ContractDto,
  type Discount,
  formatMoney,
  type MeDto,
  PAYMENT_METHOD_LABELS,
  type PaymentDto,
  type PaymentItemDto,
  type PaymentMethod,
  type TemplateDto,
  todayInWarsaw,
  VAT_RATES, datePl, looksLikeDevice, type ContractInvoicingDto, INVOICE_KINDS, PAYMENT_KINDS, PAYMENT_TRIGGERS, SETTLEMENT_DOCUMENTS, sameDayInstallmentsWarning, scheduleItemProblem, settlementDefaults } from '@bqcrm/shared';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  FileButton,
  Grid,
  Group,
  Menu,
  Modal,
  NumberInput,
  Select,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
  Textarea } from '@mantine/core';
import { IconAlertTriangle, IconCash, IconCheck, IconChevronDown, IconDots, IconEdit, IconFileTypePdf, IconPencil, IconPlus, IconPrinter, IconSignature, IconTrash, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api } from '../../api';
import { CompanyBadgeById, ContractStatusBadge, PaymentStatusBadge, TradeBadge } from '../../components/Badges';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState, SectionCard, UnsavedHint } from '../../components/ui';
import { useSaveShortcut } from '../../hooks';
import { Money, MoneyInput } from '../../components/Money';
import { PartyPicker } from '../../components/PartyPicker';
import { PaymentDialog } from '../../components/PaymentDialog';
import { LastReminder, PaymentReminderButton } from '../../components/PaymentReminder';
import { InvoicesPanel, PaymentInvoiceCell, ScheduleDocCell } from '../../components/InvoicesPanel';
import { can } from '../../auth';
import { QueryState } from '../../components/QueryState';
import { DocumentPreview, RichEditor } from '../../components/RichEditor';
import { useCase } from '../../data';
import { formatDateTime, pctPl } from '../../format';
import { notifyError, notifyOk } from '../../notify';
import { EsignPanel, SendDialog } from './EsignPanel';
import { PaperSignPanel } from './PaperSignPanel';
import { AnnexPlannedWorkAlert } from './AnnexPlannedWorkAlert';
import { AmountCalculator } from './AmountCalculator';
import { IssueWarnings } from '../../components/IssueWarnings';
import { ask } from '../../confirm';
import { minHint } from '../../minHint';


interface Item {
  key: number;
  scopeId: string;
  name: string;
  description: string;
  unit: string;
  quantity: number;
  unitPriceGr: number;
  discount: Discount | null;
  vatRate: number;
  catalogItemId: string | null;
  /** urządzenie — lista numerów seryjnych w protokole odbioru (D4) */
  device: boolean;
}
interface Pay {
  key: number;
  name: string;
  amountGr: number;
  // ustawienia rozliczenia (zachowywane przy zapisie; nowa pozycja — domyślne firmy)
  kind?: PaymentItemDto['kind'];
  trigger?: PaymentItemDto['trigger'];
  documentRequired?: PaymentItemDto['documentRequired'];
  requiredForClose?: boolean;
  dueDate: string | null;
  conditionsStart: boolean;
  payerLabel: string;
  /** QA P13: dni na zapłatę od zdarzenia (pozycja bez daty); null — ustawienie firmy */
  eventDays: number | null;
}
let n = 0;
const nk = () => ++n;

/** Ustawienia rozliczenia pozycji: zdarzenie (zadanie księgowości), wymagany dokument, wymagana do zamknięcia sprawy. */
function SettlementModal({ p, onClose, onSaved }: { p: PaymentItemDto; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ trigger: p.trigger, documentRequired: p.documentRequired, requiredForClose: p.requiredForClose });
  const m = useMutation({
    mutationFn: () => api<PaymentItemDto>(`/payment-items/${p.id}/settlement`, { method: 'PUT', body: f }),
    onSuccess: () => {
      notifyOk('Zapisano ustawienia rozliczenia');
      onSaved();
      onClose();
    },
    onError: notifyError,
  });
  const opts = (o: Record<string, string>) => Object.entries(o).map(([value, label]) => ({ value, label }));
  return (
    <Modal opened onClose={onClose} title={`Rozliczenie: ${p.name}`}>
      <Stack>
        <Text size="sm" c="dimmed">
          Proces księgowości — nie zmienia treści umowy. Gdy zajdzie wybrane zdarzenie, a pozycja nie jest rozliczona (wpłata + wymagany dokument), księgowość dostaje zadanie.
        </Text>
        <Select label="Kiedy rozliczyć (zadanie dla księgowości)" data={opts(PAYMENT_TRIGGERS)} value={f.trigger} allowDeselect={false} onChange={(v) => v && setF({ ...f, trigger: v as PaymentItemDto['trigger'] })} />
        <Select label="Wymagany dokument" data={opts(SETTLEMENT_DOCUMENTS)} value={f.documentRequired} allowDeselect={false} onChange={(v) => v && setF({ ...f, documentRequired: v as PaymentItemDto['documentRequired'] })} />
        <Checkbox label="Wymagana do zamknięcia sprawy" checked={f.requiredForClose} onChange={(e) => setF({ ...f, requiredForClose: e.currentTarget.checked })} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button loading={m.isPending} onClick={() => m.mutate()}>
            Zapisz
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function SettlementCell({ p, onEdit }: { p: PaymentItemDto; onEdit?: () => void }) {
  const s = p.settlement;
  return (
    <Stack gap={2}>
      <Group gap={4} wrap="nowrap">
        <Text size="xs">{PAYMENT_TRIGGERS[p.trigger]}</Text>
        {onEdit && p.status !== 'anulowana' && (
          <ActionIcon size="xs" variant="subtle" onClick={onEdit} aria-label="Ustawienia rozliczenia">
            <IconPencil size={12} />
          </ActionIcon>
        )}
      </Group>
      <Text size="xs" c="dimmed">
        {SETTLEMENT_DOCUMENTS[p.documentRequired]}
        {!p.requiredForClose && ' · nie blokuje zamknięcia'}
      </Text>
      {s && p.status !== 'anulowana' && (
        <Badge size="xs" variant="light" color={s.settled ? 'green' : s.triggered ? 'orange' : 'gray'}>
          {s.settled ? 'rozliczona' : `${s.triggered ? 'do rozliczenia' : 'później'} — brak: ${s.missing.join(', ')}`}
        </Badge>
      )}
    </Stack>
  );
}

function ScheduleTable({ rows, canPay, onPay, docCell, onSettle }: { rows: PaymentItemDto[]; canPay: boolean; onPay: (p: PaymentItemDto) => void; docCell?: (p: PaymentItemDto) => ReactNode; onSettle?: (p: PaymentItemDto) => void }) {
  if (!rows.length)
    return (
      <Card padding={0}>
        <EmptyState compact title="Brak płatności w harmonogramie" />
      </Card>
    );
  // na telefonie tabela przewija się w poziomie zamiast rozpychać stronę
  return (
    <Card padding={0}>
    <Table.ScrollContainer minWidth={docCell ? 900 : 780}>
    <Table highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Płatność</Table.Th>
          <Table.Th ta="right">Kwota</Table.Th>
          <Table.Th ta="right">Wpłacono</Table.Th>
          <Table.Th>Termin</Table.Th>
          <Table.Th>Stan</Table.Th>
          <Table.Th>Rozliczenie</Table.Th>
          {docCell && <Table.Th>Dokument</Table.Th>}
          <Table.Th />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((p) => (
          <Table.Tr key={p.id} style={{ opacity: p.status === 'anulowana' ? 0.5 : 1 }}>
            <Table.Td>
              <Text size="sm">{p.name}</Text>
              <Group gap={4}>
                {p.conditionsStart && (
                  <Badge size="xs" variant="light" color="orange">
                    warunek startu
                  </Badge>
                )}
                {p.payerLabel && (
                  <Badge size="xs" variant="light">
                    płatnik: {p.payerLabel}
                  </Badge>
                )}
                {p.cancelledByNumber && (
                  <Badge size="xs" variant="light" color="dark">
                    anulowana aneksem {p.cancelledByNumber}
                  </Badge>
                )}
              </Group>
            </Table.Td>
            <Table.Td ta="right">
              <Money gr={p.amountGr} />
            </Table.Td>
            <Table.Td ta="right">
              <Money gr={p.paidGr} />
            </Table.Td>
            <Table.Td>
              <Text size="sm" c={p.overdue ? 'var(--app-text-red)' : undefined} fw={p.overdue ? 600 : 400}>
                {p.dueDate ? datePl(p.dueDate) : `${PAYMENT_TRIGGERS[p.trigger].toLowerCase()}${p.eventDays !== null ? ` + ${p.eventDays} dni` : ''}`}
                {p.overdue && ' — po terminie'}
              </Text>
              {p.dueFromEvent && (
                <Text size="xs" c="dimmed">
                  ustalony po zdarzeniu
                </Text>
              )}
              {/* QA P4: po wystawieniu faktury / pro formy obowiązuje termin z dokumentu */}
              {p.invoiceDue && p.invoiceDue.date !== p.dueDate && (
                <Text size="xs" c={p.overdue ? 'var(--app-text-red)' : 'dimmed'}>
                  obowiązuje termin z faktury {p.invoiceDue.number ?? ''}: {datePl(p.invoiceDue.date)}
                </Text>
              )}
            </Table.Td>
            <Table.Td>
              <PaymentStatusBadge status={p.status} overdue={p.overdue} />
              <LastReminder r={p.lastReminder} />
            </Table.Td>
            <Table.Td>
              <SettlementCell p={p} onEdit={onSettle ? () => onSettle(p) : undefined} />
            </Table.Td>
            {docCell && <Table.Td>{p.status !== 'anulowana' && docCell(p)}</Table.Td>}
            <Table.Td>
              {canPay && (p.status === 'oczekuje' || p.status === 'czesciowo') && (
                <Stack gap={2} align="flex-start">
                  <Button size="compact-xs" variant="light" leftSection={<IconCash size={14} />} onClick={() => onPay(p)}>
                    Zarejestruj wpłatę
                  </Button>
                  <PaymentReminderButton itemId={p.id} />
                </Stack>
              )}
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
    </Table.ScrollContainer>
    </Card>
  );
}

/** Zakładka z adresu — tylko istniejące (faktury i aneksy tylko tam, gdzie są pokazywane). */
function tabFromLink(v: string | null, invoices: boolean, annexes: boolean): string | null {
  if (v === 'dane' || v === 'tresc' || v === 'platnosci') return v;
  if (v === 'faktury' && invoices) return v;
  if (v === 'aneksy' && annexes) return v;
  return null;
}

export function ContractPage({ me }: { me: MeDto }) {
  const { id = '' } = useParams();
  // ?zakladka=faktury|platnosci|… — otwarcie zakładki z linku (np. ze strony Finanse)
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['contract', id], queryFn: () => api<ContractDto>(`/contracts/${id}`) });
  const k = q.data;
  const caseQ = useCase(k?.caseId ?? '');
  const templates = useQuery({ queryKey: ['templates', k?.kind], queryFn: () => api<TemplateDto[]>(`/templates?kind=${k!.kind}`), enabled: !!k && k.status === 'roboczy' });
  const parent = useQuery({ queryKey: ['contract', k?.parentId], queryFn: () => api<ContractDto>(`/contracts/${k!.parentId}`), enabled: !!k?.parentId });

  const [form, setForm] = useState<{ title: string; payerId: string; payerName: string; executionTerm: string; depositRequired: boolean; templateVersionId: string | null; bodyHtml: string } | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [schedule, setSchedule] = useState<Pay[]>([]);
  const [cancelIds, setCancelIds] = useState<string[]>([]);
  // aneks: pozycje to dodatkowe prace (po podpisie — planowanie i odbiór), albo tylko zmiana cen / warunków
  const [annexWorks, setAnnexWorks] = useState(true);
  const [baseline, setBaseline] = useState('');
  const [dialog, setDialog] = useState<null | 'preview' | 'sign' | 'cancel' | 'pay' | 'reverse' | 'esign' | 'changes' | 'toContract'>(null);
  // QA P3: po „Wydaj ponownie do e-podpisu” (?esign=1) — od razu okno wysyłki nowej wersji
  useEffect(() => {
    if (k?.id !== id || searchParams.get('esign') !== '1') return;
    if (k.status === 'wydany' && k.can.esign) setDialog('esign');
    navigate(`/umowy/${id}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [k?.id]);
  const [changesNote, setChangesNote] = useState('');
  const [settling, setSettling] = useState<PaymentItemDto | null>(null);
  const [preview, setPreview] = useState<{ html: string; missing: string[] } | null>(null);
  const [sign, setSign] = useState<{ signedAt: string; signedBy: string; note: string; file: File | null }>({ signedAt: todayInWarsaw(), signedBy: '', note: '', file: null });
  const [reason, setReason] = useState('');
  const [pay, setPay] = useState<{ item: PaymentItemDto | null; amountGr: number | null; paidAt: string; method: PaymentMethod; reference: string }>({ item: null, amountGr: null, paidAt: todayInWarsaw(), method: 'przelew', reference: '' });
  const [reverseOf, setReverseOf] = useState<PaymentDto | null>(null);

  const snapshot = () => JSON.stringify({ form, items, schedule, cancelIds, annexWorks });
  useEffect(() => {
    if (!k) return;
    const f = { title: k.title, payerId: k.payer.id, payerName: k.payer.displayName, executionTerm: k.executionTerm, depositRequired: k.depositRequired, templateVersionId: k.templateVersionId, bodyHtml: k.bodyHtml };
    const its = k.items.map((i) => ({ key: nk(), ...i }));
    const own = k.schedule.filter((s) => s.contractId === k.id).map((s) => ({ key: nk(), name: s.name, amountGr: s.amountGr, dueDate: s.dueDate, conditionsStart: s.conditionsStart, payerLabel: s.payerLabel, kind: s.kind, trigger: s.trigger, documentRequired: s.documentRequired, requiredForClose: s.requiredForClose, eventDays: s.eventDays }));
    setForm(f);
    setItems(its);
    setSchedule(own);
    setCancelIds([]);
    setAnnexWorks(k.annexWorks ?? true);
    setBaseline(JSON.stringify({ form: f, items: its, schedule: own, cancelIds: [], annexWorks: k.annexWorks ?? true }));
  }, [k]);

  const totals = useMemo(
    () => computeVariant(items.map((i) => ({ quantity: i.quantity, unitPriceGr: i.unitPriceGr, listPriceGr: i.unitPriceGr, discount: i.discount, vatRate: i.vatRate, purchasePriceGr: null })), null),
    [items],
  );
  const scheduleSum = schedule.reduce((a, s) => a + s.amountGr, 0);
  const dirty = !!form && snapshot() !== baseline;
  const editable = !!k?.can.edit;
  const refresh = (c?: ContractDto) => {
    if (c) qc.setQueryData(['contract', c.id], c);
    else void qc.invalidateQueries({ queryKey: ['contract', id] });
    void qc.invalidateQueries({ queryKey: ['case-contracts'] });
    void qc.invalidateQueries({ queryKey: ['start-conditions'] });
    if (k) void qc.invalidateQueries({ queryKey: ['case', k.caseId] });
  };
  const run = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: (r) => {
      refresh(r && typeof r === 'object' && 'id' in (r as object) && 'kind' in (r as object) ? (r as ContractDto) : undefined);
      setDialog(null);
      setReason('');
    },
    onError: notifyError,
  });
  const save = () =>
    run.mutate(async () => {
      const r = await api<ContractDto>(`/contracts/${id}`, {
        method: 'PUT',
        body: {
          title: form!.title,
          payerId: form!.payerId,
          executionTerm: form!.executionTerm,
          depositRequired: form!.depositRequired,
          templateVersionId: form!.templateVersionId,
          bodyHtml: form!.bodyHtml,
          items: items.map(({ key: _k, ...i }) => i),
          schedule: schedule.map(({ key: _k, ...s }) => s),
          cancelScheduleItemIds: cancelIds,
          annexWorks,
        },
      });
      notifyOk('Zapisano umowę roboczą');
      return r;
    });
  useSaveShortcut(dirty && editable && !run.isPending, save);
  const openPreview = async () => {
    try {
      setPreview(await api<{ html: string; missing: string[] }>(`/contracts/${id}/preview`));
      setDialog('preview');
    } catch (e) {
      notifyError(e);
    }
  };
  const submitSign = () =>
    run.mutate(async () => {
      const fd = new FormData();
      fd.append('signedAt', sign.signedAt);
      fd.append('signedBy', sign.signedBy);
      fd.append('note', sign.note);
      if (sign.file) fd.append('scan', sign.file);
      const res = await fetch(`/api/contracts/${id}/sign`, { method: 'POST', body: fd, credentials: 'same-origin' });
      const data = await res.json();
      if (!res.ok) throw new Error([data.message, ...(data.details ?? [])].join('\n'));
      notifyOk('Odnotowano podpis');
      return data as ContractDto;
    });
  const scope = (sid: string) => caseQ.data?.scopes.find((s) => s.id === sid);
  const parentCancelable = (parent.data?.schedule ?? []).filter((p) => p.status === 'oczekuje' && p.paidGr === 0);
  // faktury prowadzi się na umowie głównej; po anulowaniu zostają widoczne (korekty)
  const showInvoices = !!k && k.kind === 'umowa' && k.status !== 'roboczy' && (can(me, 'faktury.podglad') || can(me, 'faktury.wystawianie'));  // faktury umowy — do ostrzeżeń przy stornie i anulowaniu (wystawionej faktury nie da się usunąć, tylko skorygować)
  const invQ = useQuery({ queryKey: ['contract-invoicing', id], queryFn: () => api<ContractInvoicingDto>(`/contracts/${id}/invoicing`), enabled: showInvoices, retry: false });
  const issuedInvoices = (invQ.data?.invoices ?? []).filter((i) => i.status === 'wystawiona' && ['zaliczkowa', 'koncowa', 'vat'].includes(i.kind));
  const correctedIds = new Set((invQ.data?.invoices ?? []).filter((i) => i.kind === 'korekta' && i.status === 'wystawiona').map((i) => i.parentNumber));
  const uncorrected = issuedInvoices.filter((i) => !correctedIds.has(i.number));
  const reverseInvoice = reverseOf ? invQ.data?.payments.find((p) => p.paymentId === reverseOf.id)?.invoice : undefined;
  // nazwy dokumentu w przyciskach i oknach („Anuluj umowę”, „Usunięcie aneksu roboczego”)
  const isAnnex = k?.kind === 'aneks';
  const cancelLabel = k?.status === 'roboczy' ? (isAnnex ? 'Usuń aneks roboczy' : 'Usuń umowę roboczą') : isAnnex ? 'Anuluj aneks' : 'Anuluj umowę';
  const cancelTitle = k?.status === 'roboczy' ? (isAnnex ? 'Usunięcie aneksu roboczego' : 'Usunięcie umowy roboczej') : isAnnex ? 'Anulowanie aneksu' : 'Anulowanie umowy';
  // zlecenie wg oferty (szybka ścieżka poniżej progu): bez dokumentu do podpisu
  const isOrder = k?.basis === 'oferta';
  const hasMore = !!k && (k.can.annex || k.can.changes || k.can.cancel || k.can.toContract);

  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {k && form && (
        <div className={editable ? undefined : "doc-readonly"}>
          <PageHeader
            crumbs={[
              { label: 'Sprawy', to: '/sprawy' },
              { label: k.caseNumber, to: `/sprawy/${k.caseId}?zakladka=umowy` },
              ...(k.parentId ? [{ label: `Umowa ${k.parentNumber ?? ''}`.trim(), to: `/umowy/${k.parentId}` }] : []),
              { label: isAnnex ? 'Aneks' : isOrder ? 'Zlecenie wg oferty' : 'Umowa' },
            ]}
            title={k.number ?? (isAnnex ? `Aneks do ${k.parentNumber}` : 'Umowa (numer przy wydaniu)')}
            badge={
              <Group gap={6} wrap="wrap">
                {isOrder ? (
                  <Badge variant="light" color={k.status === 'anulowany' ? 'gray' : 'teal'}>
                    {k.status === 'anulowany' ? 'Zlecenie anulowane' : 'Zlecenie wg oferty'}
                  </Badge>
                ) : (
                  <ContractStatusBadge status={k.status} />
                )}
                <CompanyBadgeById id={k.companyId} full />
                {k.scopeIds.map((s) => scope(s) && <TradeBadge key={s} code={scope(s)!.tradeCode} />)}
              </Group>
            }
            description={k.caseTitle}
            actions={
            <>
              <UnsavedHint show={dirty} action="zobaczyć podgląd, PDF albo wydać umowę" />
              {!isOrder && (
                <>
                  <Button variant="default" onClick={() => void openPreview()} disabled={dirty}>
                    Podgląd treści
                  </Button>
                  <Button variant="default" leftSection={<IconFileTypePdf size={16} />} component="a" href={dirty ? undefined : `/api/contracts/${id}/pdf`} target="_blank" disabled={dirty} title={dirty ? 'Zapisz zmiany, aby zobaczyć PDF' : undefined}>
                    PDF
                  </Button>
                </>
              )}
              {editable && (
                <Button variant="light" onClick={save} loading={run.isPending} disabled={!dirty}>
                  Zapisz
                </Button>
              )}
              {k.can.issue && (
                <Menu position="bottom-end" width={340} disabled={dirty}>
                  <Menu.Target>
                    <Button disabled={dirty} loading={run.isPending} rightSection={<IconChevronDown size={16} />}>
                      Wydaj do podpisu
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {k.can.esign && (
                      <Menu.Item leftSection={<IconSignature size={18} />} onClick={() => setDialog('esign')}>
                        <Text size="sm" fw={500}>
                          Podpis elektroniczny
                        </Text>
                        <Text size="xs" c="dimmed">
                          Wydaj i od razu wyślij klientowi link do podpisu
                        </Text>
                      </Menu.Item>
                    )}
                    <Menu.Item
                      leftSection={<IconPrinter size={18} />}
                      onClick={() =>
                        void ask(
                          k.issueWarnings.length ? (
                            <Stack gap="xs">
                              <Text size="sm">Treść zostanie zamrożona i otrzyma numer. Dalsze zmiany — tylko aneksem.</Text>
                              <Alert color="orange" variant="light" icon={<IconAlertTriangle />}>
                                <IssueWarnings items={k.issueWarnings} />
                              </Alert>
                            </Stack>
                          ) : (
                            'Treść zostanie zamrożona i otrzyma numer. Dalsze zmiany — tylko aneksem.'
                          ),
                          { title: 'Wydać do podpisu papierowego?', confirm: 'Wydaj' },
                        ).then(
                          (ok) =>
                            ok &&
                            run.mutate(async () => {
                              const r = await api<ContractDto>(`/contracts/${id}/issue`, { method: 'POST' });
                              notifyOk(`Wydano do podpisu: ${r.number ?? (isAnnex ? 'aneks' : 'umowa')}`);
                              return r;
                            }),
                        )
                      }
                    >
                      <Text size="sm" fw={500}>
                        Podpis papierowy
                      </Text>
                      <Text size="xs" c="dimmed">
                        Wydaj, wydrukuj PDF, a po podpisaniu odnotuj podpis ze skanem
                      </Text>
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              )}
              {k.can.sign && (
                <Button leftSection={<IconSignature size={16} />} onClick={() => setDialog('sign')}>
                  Odnotuj podpis
                </Button>
              )}
              {hasMore && (
                <Menu position="bottom-end" withinPortal>
                  <Menu.Target>
                    <ActionIcon variant="default" size="lg" aria-label="Więcej">
                      <IconDots size={16} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {k.can.annex && (
                      <Menu.Item
                        leftSection={<IconPlus size={14} />}
                        onClick={() =>
                          run.mutate(async () => {
                            const a = await api<ContractDto>(`/contracts/${id}/annexes`, { method: 'POST' });
                            notifyOk('Utworzono aneks roboczy');
                            navigate(`/umowy/${a.id}`);
                          })
                        }
                      >
                        Nowy aneks
                      </Menu.Item>
                    )}
                    {k.can.changes && (
                      <Menu.Item leftSection={<IconEdit size={14} />} color="yellow" onClick={() => setDialog('changes')}>
                        Klient zgłosił zmiany
                      </Menu.Item>
                    )}
                    {k.can.toContract && (
                      <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => setDialog('toContract')}>
                        Klient chce umowę
                      </Menu.Item>
                    )}
                    {k.can.cancel && (
                      <Menu.Item leftSection={k.status === 'roboczy' ? <IconTrash size={14} /> : <IconX size={14} />} color="red" onClick={() => setDialog('cancel')}>
                        {cancelLabel}
                      </Menu.Item>
                    )}
                  </Menu.Dropdown>
                </Menu>
              )}
            </>
            }
          />

          {isOrder && k.status === 'podpisany' && (
            <Alert color="teal" variant="light" mb="md" title="Realizacja na podstawie oferty — bez umowy">
              Wartość zaakceptowanych ofert firmy jest poniżej progu umowy (Ustawienia → Firmy). Zlecenie powstało przy akceptacji oferty
              {k.acceptedOfferNumbers.length ? ` ${k.acceptedOfferNumbers.join(', ')}` : ''} — prace można od razu planować. Płatność po odbiorze: fakturę
              wystawia księgowość po podpisaniu protokołu odbioru.
            </Alert>
          )}
          {k.previous && k.status === 'roboczy' && (
            <Alert color="yellow" mb="md" title="Nowa wersja po uwagach klienta">
              Zastępuje{' '}
              <Anchor component={Link} to={`/umowy/${k.previous.id}`} size="sm">
                {k.previous.number ?? 'poprzednią wersję'}
              </Anchor>
              . Popraw treść, pozycje lub harmonogram i wydaj ponownie do podpisu.
            </Alert>
          )}
          {k.previous && (k.status === 'wydany' || k.status === 'podpisany') && (
            <Text size="sm" c="dimmed" mb="md">
              Nowa wersja — zastępuje anulowany dokument{' '}
              <Anchor component={Link} to={`/umowy/${k.previous.id}`} size="sm">
                {k.previous.number ?? 'poprzednią wersję'}
              </Anchor>
              .
            </Text>
          )}
          {k.status === 'anulowany' && k.cancelKind === 'zmiany' ? (
            <Alert color="yellow" mb="md" title="Wymaga zmian — zastąpiona nową wersją">
              {formatDateTime(k.cancelledAt)}: {k.cancelReason.replace(/^Wymaga zmian: /, '')}
              {k.replacedBy && (
                <>
                  {' · '}
                  <Anchor component={Link} to={`/umowy/${k.replacedBy.id}`} size="sm">
                    nowa wersja {k.replacedBy.number ?? '(robocza)'}
                  </Anchor>
                </>
              )}
            </Alert>
          ) : null}
          {k.status === 'anulowany' && k.cancelKind !== 'zmiany' && (
            <Alert color="red" mb="md" title="Dokument anulowany">
              {formatDateTime(k.cancelledAt)}: {k.cancelReason}
              {k.payments.some((p) => !p.reversalOf && !p.reversedBy) && ' · Uwaga: dokument ma wpłaty — do rozliczenia z księgowością.'}
            </Alert>
          )}
          {k.status === 'podpisany' && !isOrder && (
            <Alert color="green" icon={<IconCheck />} mb="md" title={`Podpisano ${datePl(k.signedAt)}`}>
              {k.signedBy}
              {k.signedNote ? ` · ${k.signedNote}` : ''} ·{' '}
              <Anchor href={`/api/contracts/${id}/scan`} target="_blank" size="sm">
                {k.scanFileName?.endsWith('-podpisany.pdf') ? 'dokument podpisany elektronicznie' : 'skan'}: {k.scanFileName}
              </Anchor>
              <Text size="xs" mt={4}>
                Treść jest niezmienna — zmiany wprowadza się aneksem.
              </Text>
            </Alert>
          )}
          {k.status === 'wydany' && (
            <Alert color="blue" mb="md">
              Wydano {formatDateTime(k.issuedAt)} ({k.issuedByName}){k.issueMode === 'epodpis' ? ' do podpisu elektronicznego' : k.issueMode === 'papier' ? ' do podpisu papierowego' : ''}.
              {k.can.sign ? ' Wyślij dokument do podpisu elektronicznego albo wydrukuj PDF i po podpisaniu odnotuj podpis ze skanem.' : ''}
            </Alert>
          )}
          <PaperSignPanel k={k} onEsign={k.can.esign ? () => setDialog('esign') : undefined} />
          <EsignPanel k={k} />
          {isAnnex && (k.status === 'roboczy' || k.status === 'wydany') && <AnnexPlannedWorkAlert annexId={k.id} caseId={k.caseId} issued={k.status === 'wydany'} />}
          {k.missingFields.length > 0 && (
            <Alert color="red" icon={<IconAlertTriangle />} mb="md" title="Treść zawiera nieznane pola">
              {k.missingFields.join(', ')} — popraw treść, aby wydać dokument.
            </Alert>
          )}
          {k.issueWarnings.length > 0 && (
            <Alert color="orange" icon={<IconAlertTriangle />} mb="md" title={k.status === 'roboczy' ? 'Przed wydaniem sprawdź' : 'Uwaga do wydanej umowy'}>
              <IssueWarnings items={k.issueWarnings} />
            </Alert>
          )}
          {!k.can.seeAmounts && (
            <Alert variant="light" mb="md">
              Kwoty są ukryte — Twoja rola nie ma dostępu do danych finansowych.
            </Alert>
          )}

          <Tabs defaultValue={tabFromLink(searchParams.get('zakladka'), showInvoices, k.kind === 'umowa') ?? (k.status === 'roboczy' ? 'dane' : 'platnosci')} keepMounted={false}>
            <Tabs.List mb="md">
              <Tabs.Tab value="dane">Dane i przedmiot</Tabs.Tab>
              {!isOrder && <Tabs.Tab value="tresc">Treść dokumentu</Tabs.Tab>}
              <Tabs.Tab value="platnosci">Harmonogram i wpłaty</Tabs.Tab>
              {k.kind === 'umowa' && !isOrder && <Tabs.Tab value="aneksy">Aneksy ({k.annexes.length})</Tabs.Tab>}
              {showInvoices && <Tabs.Tab value="faktury">Faktury</Tabs.Tab>}
            </Tabs.List>

            <Tabs.Panel value="dane">
              <Grid gap="lg">
                <Grid.Col span={{ base: 12, lg: 4 }}>
                  <Card withBorder>
                    <Stack gap="xs">
                      <TextInput label="Tytuł" value={form.title} disabled={!editable} onChange={(e) => setForm({ ...form, title: e.currentTarget.value })} />
                      {editable ? (
                        <PartyPicker label="Płatnik" value={form.payerId} initialLabel={form.payerName} onChange={(v) => v && setForm({ ...form, payerId: v })} />
                      ) : (
                        <TextInput label="Płatnik" value={k.payer.displayName} disabled />
                      )}
                      <TextInput label="Termin realizacji" placeholder="np. do 6 tygodni od podpisania umowy" value={form.executionTerm} disabled={!editable} onChange={(e) => setForm({ ...form, executionTerm: e.currentTarget.value })} />
                      {k.kind === 'umowa' && (
                        <Switch
                          label="Rozpoczęcie prac wymaga zaliczki"
                          description="Płatności oznaczone „warunek startu” muszą być opłacone, zanim ekipa rozpocznie prace."
                          checked={form.depositRequired}
                          disabled={!editable}
                          onChange={(e) => setForm({ ...form, depositRequired: e.currentTarget.checked })}
                        />
                      )}
                      {editable ? (
                        <Select
                          label="Szablon"
                          data={(templates.data ?? []).filter((t) => t.active).map((t) => ({ value: t.latest.id, label: `${t.name} (v${t.latest.versionNo})` }))}
                          value={form.templateVersionId}
                          onChange={(v) => {
                            const t = templates.data?.find((x) => x.latest.id === v);
                            if (t) void ask('Ręczne zmiany w treści umowy zostaną utracone.', { title: 'Zastąpić treść szablonem?', confirm: 'Zastąp', danger: true }).then((ok) => ok && setForm({ ...form, templateVersionId: v, bodyHtml: t.latest.bodyHtml }));
                          }}
                        />
                      ) : (
                        <TextInput label="Szablon" value={k.templateName ?? '—'} disabled />
                      )}
                      {k.acceptedOfferNumbers.length > 0 && (
                        <Text size="xs" c="dimmed">
                          Na podstawie akceptacji: {k.acceptedOfferNumbers.join(', ')}
                        </Text>
                      )}
                      {k.offerTerms.length > 0 && (
                        <Alert variant="light" color="blue" p="xs" title="Ustalenia z oferty">
                          {k.offerTerms.map((t) => (
                            <Text key={t.offerNumber} size="xs">
                              {[
                                k.offerTerms.length > 1 ? t.offerNumber : '',
                                t.depositPct !== null ? (t.depositPct > 0 ? `zaliczka ${t.depositPct}%` : 'bez zaliczki') : '',
                                t.signForm ? CONTRACT_SIGN_FORMS[t.signForm] : '',
                                t.note,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </Text>
                          ))}
                        </Alert>
                      )}
                    </Stack>
                  </Card>
                  {k.kind === 'umowa' && (
                    <Box mt="md">
                    <SectionCard title="Załączniki">
                      <Text size="xs" c="dimmed" mb="xs">
                        Zaakceptowana oferta jest zawsze dołączana do umowy — na liście w treści i jako pełny PDF po treści umowy (także w dokumencie do podpisu elektronicznego).
                        {k.status !== 'roboczy' && ' Lista została zapisana przy wydaniu.'}
                      </Text>
                      {k.attachments.length === 0 ? (
                        k.status === 'roboczy' ? (
                          <EmptyState compact title="Brak zaakceptowanej oferty" hint="Dla zakresów tej umowy nie ma zaakceptowanej oferty — umowa nie będzie miała załącznika." />
                        ) : (
                          <EmptyState compact title="Brak załączników" />
                        )
                      ) : (
                        <Stack gap={4}>
                          {k.attachments.map((a, n) => (
                            <Group key={a.versionId} gap="xs" wrap="nowrap">
                              <Text size="sm">
                                {n + 1}. Oferta{' '}
                                <Anchor component={Link} to={`/oferty/${a.offerId}`} size="sm">
                                  {a.number}
                                </Anchor>{' '}
                                — wariant „{a.variantName}”{a.grossGr !== null ? `, ${formatMoney(a.grossGr)} brutto` : ''}, zaakceptowana {formatDateTime(a.acceptedAt)} ({a.personName})
                              </Text>
                              <Anchor href={`/api/offer-versions/${a.versionId}/pdf`} target="_blank" size="xs">
                                PDF
                              </Anchor>
                            </Group>
                          ))}
                        </Stack>
                      )}
                    </SectionCard>
                    </Box>
                  )}
                </Grid.Col>
                <Grid.Col span={{ base: 12, lg: 8 }}>
                  {k.can.seeAmounts && (
                    <SectionCard
                      title="Przedmiot umowy"
                      actions={
                        k.acceptedGrossGr !== null && (
                          <Badge size="sm" color={k.acceptedGrossGr === totals.grossGr ? 'green' : 'orange'} variant="light">
                            {k.acceptedGrossGr === totals.grossGr ? 'Zgodny z zaakceptowaną ofertą' : `Różni się od akceptacji (${formatMoney(k.acceptedGrossGr)})`}
                          </Badge>
                        )
                      }
                    >
                      <Table.ScrollContainer minWidth={700}>
                        <Table verticalSpacing={4}>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Pozycja</Table.Th>
                              <Table.Th w={80}>Ilość</Table.Th>
                              <Table.Th w={130} ta="right">
                                Cena netto
                              </Table.Th>
                              <Table.Th w={80}>VAT</Table.Th>
                              <Table.Th w={120} ta="right">
                                Wartość netto
                              </Table.Th>
                              {editable && <Table.Th w={30} />}
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {items.map((i, idx) => (
                              <Table.Tr key={i.key}>
                                <Table.Td>
                                  <TextInput size="xs" value={i.name} disabled={!editable} onChange={(e) => setItems(items.map((x) => (x.key === i.key ? { ...x, name: e.currentTarget.value } : x)))} />
                                  <Checkbox
                                    size="xs"
                                    mt={4}
                                    label="urządzenie (numer seryjny w protokole odbioru)"
                                    checked={i.device}
                                    disabled={!editable}
                                    onChange={(e) => setItems(items.map((x) => (x.key === i.key ? { ...x, device: e.currentTarget.checked } : x)))}
                                  />
                                  {/* P20: nazwa wskazuje urządzenie (magazyn, falownik, pompa…) — podpowiedź, bez zmiany oznaczenia */}
                                  {editable && !i.device && !i.catalogItemId && looksLikeDevice(i.name) && (
                                    <Text size="xs" c="orange">
                                      Nazwa wskazuje urządzenie — zaznacz, żeby trafiło na listę protokołu odbioru i do karty instalacji.
                                    </Text>
                                  )}
                                  {i.discount && (
                                    <Text size="xs" c="dimmed">
                                      rabat {i.discount.type === 'procent' ? pctPl(i.discount.value) : formatMoney(i.discount.value)}
                                    </Text>
                                  )}
                                </Table.Td>
                                <Table.Td>
                                  <NumberInput size="xs" hideControls decimalScale={3} min={0.001} value={i.quantity} disabled={!editable} onChange={(v) => setItems(items.map((x) => (x.key === i.key ? { ...x, quantity: Number(v) || 0 } : x)))} />
                                </Table.Td>
                                <Table.Td>
                                  <MoneyInput size="xs" valueGr={i.unitPriceGr} disabled={!editable} onChangeGr={(v) => setItems(items.map((x) => (x.key === i.key ? { ...x, unitPriceGr: v ?? 0 } : x)))} />
                                </Table.Td>
                                <Table.Td>
                                  <Select size="xs" data={VAT_RATES.map((r) => ({ value: String(r), label: `${r}%` }))} value={String(i.vatRate)} disabled={!editable} allowDeselect={false} onChange={(v) => v && setItems(items.map((x) => (x.key === i.key ? { ...x, vatRate: Number(v) } : x)))} />
                                </Table.Td>
                                <Table.Td ta="right">
                                  <Money gr={totals.items[idx]?.netGr} currency={false} />
                                </Table.Td>
                                {editable && (
                                  <Table.Td>
                                    <ActionIcon variant="subtle" color="red" onClick={() => setItems(items.filter((x) => x.key !== i.key))} aria-label="Usuń pozycję">
                                      <IconTrash size={14} />
                                    </ActionIcon>
                                  </Table.Td>
                                )}
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </Table.ScrollContainer>
                      {editable && (
                        <Button
                          size="xs"
                          variant="light"
                          mt="xs"
                          leftSection={<IconPlus size={14} />}
                          onClick={() => setItems([...items, { key: nk(), scopeId: k.scopeIds[0]!, name: 'Pozycja', description: '', unit: 'usł.', quantity: 1, unitPriceGr: 0, discount: null, vatRate: 8, catalogItemId: null, device: false }])}
                        >
                          Dodaj pozycję
                        </Button>
                      )}
                      <Group justify="flex-end" mt="sm" gap="xl">
                        <Text size="sm">
                          Netto <Money gr={totals.netGr} />
                        </Text>
                        <Text size="sm">
                          VAT <Money gr={totals.vatGr} />
                        </Text>
                        <Text fw={700}>
                          Brutto <Money gr={totals.grossGr} />
                        </Text>
                      </Group>
                    </SectionCard>
                  )}
                </Grid.Col>
              </Grid>
            </Tabs.Panel>

            <Tabs.Panel value="tresc">
              {editable ? (
                <RichEditor value={form.bodyHtml} onChange={(v) => setForm({ ...form, bodyHtml: v })} />
              ) : (
                <Button variant="light" onClick={() => void openPreview()}>
                  Pokaż wydaną treść
                </Button>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="platnosci">
              {editable && k.can.seeAmounts ? (
                <SectionCard title={k.kind === 'aneks' ? 'Płatności dodawane aneksem' : 'Harmonogram płatności'}>
                  {!schedule.length && <EmptyState compact title="Brak płatności" hint="Dodaj płatności harmonogramu — kwoty brutto z terminami." />}
                  {schedule.map((s) => {
                    // zdarzenie: wybrane albo domyślne (z nazwy: „po montażu”, „po odbiorze”…) — serwer przyjmie to samo
                    // P12: typ pozycji i wymagany dokument — wyliczone z nazwy / „warunku startu” i ustawień firmy, do zmiany
                    const defaults = settlementDefaults(s, k.settlementSettings ?? {});
                    const trigger = s.trigger ?? defaults.trigger;
                    const kind = s.kind ?? defaults.kind;
                    const documentRequired = s.documentRequired ?? defaults.documentRequired;
                    const problem = scheduleItemProblem({ ...s, trigger });
                    return (
                      <Box key={s.key} mb={6}>
                        {/* pola zawijają się, a przycisk usuwania zostaje zawsze po prawej w pierwszym wierszu */}
                        <Group wrap="nowrap" align="flex-start" gap="xs">
                        <Group wrap="wrap" align="flex-end" gap="xs" style={{ rowGap: 4, flex: 1, minWidth: 0 }}>
                          <TextInput size="xs" label="Nazwa" value={s.name} onChange={(e) => setSchedule(schedule.map((x) => (x.key === s.key ? { ...x, name: e.currentTarget.value } : x)))} style={{ flex: 1, minWidth: 180 }} />
                          <MoneyInput
                            size="xs"
                            label="Kwota brutto"
                            w={150}
                            valueGr={s.amountGr}
                            onChangeGr={(v) => setSchedule(schedule.map((x) => (x.key === s.key ? { ...x, amountGr: v ?? 0 } : x)))}
                            rightSection={
                              <AmountCalculator
                                totalGr={totals.grossGr}
                                othersGr={scheduleSum - s.amountGr}
                                totalLabel={k.kind === 'aneks' ? 'Wartość aneksu' : 'Wartość umowy'}
                                onApply={(gr) => setSchedule(schedule.map((x) => (x.key === s.key ? { ...x, amountGr: gr } : x)))}
                              />
                            }
                          />
                          <TextInput size="xs" label="Termin" type="date" w={150} value={s.dueDate ?? ''} error={!!problem} onChange={(e) => setSchedule(schedule.map((x) => (x.key === s.key ? { ...x, dueDate: e.currentTarget.value || null } : x)))} />
                          <Select
                            size="xs"
                            label="Kiedy rozliczyć"
                            w={220}
                            data={Object.entries(PAYMENT_TRIGGERS).map(([value, label]) => ({ value, label }))}
                            value={trigger}
                            allowDeselect={false}
                            error={!!problem}
                            onChange={(v) => v && setSchedule(schedule.map((x) => (x.key === s.key ? { ...x, trigger: v as PaymentItemDto['trigger'] } : x)))}
                          />
                          {/* QA P13: termin „N dni od zdarzenia” dla tej pozycji (bez daty) — wypisany w treści umowy */}
                          {!s.dueDate && trigger !== 'termin' && trigger !== 'reczny' && (
                            <NumberInput
                              size="xs"
                              label="Dni od zdarzenia"
                              w={120}
                              min={0}
                              max={120}
                              allowDecimal={false}
                              placeholder="wg firmy"
                              value={s.eventDays ?? ''}
                              onChange={(v) => setSchedule(schedule.map((x) => (x.key === s.key ? { ...x, eventDays: v === '' ? null : Number(v) } : x)))}
                            />
                          )}
                          <Select
                            size="xs"
                            label="Typ"
                            w={140}
                            data={Object.entries(PAYMENT_KINDS).map(([value, label]) => ({ value, label }))}
                            value={kind}
                            allowDeselect={false}
                            onChange={(v) => v && setSchedule(schedule.map((x) => (x.key === s.key ? { ...x, kind: v as PaymentItemDto['kind'] } : x)))}
                          />
                          <Select
                            size="xs"
                            label="Wymagany dokument"
                            w={230}
                            data={Object.entries(SETTLEMENT_DOCUMENTS).map(([value, label]) => ({ value, label }))}
                            value={documentRequired}
                            allowDeselect={false}
                            onChange={(v) => v && setSchedule(schedule.map((x) => (x.key === s.key ? { ...x, documentRequired: v as PaymentItemDto['documentRequired'] } : x)))}
                          />
                          <TextInput size="xs" label="Inny płatnik (np. dotacja)" w={180} value={s.payerLabel} onChange={(e) => setSchedule(schedule.map((x) => (x.key === s.key ? { ...x, payerLabel: e.currentTarget.value } : x)))} />
                          <Checkbox size="xs" label="warunek startu" checked={s.conditionsStart} onChange={(e) => setSchedule(schedule.map((x) => (x.key === s.key ? { ...x, conditionsStart: e.currentTarget.checked } : x)))} mb={6} />
                        </Group>
                          <ActionIcon variant="subtle" color="red" mt={22} onClick={() => setSchedule(schedule.filter((x) => x.key !== s.key))} aria-label="Usuń płatność" title="Usuń płatność">
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Group>
                        {problem && (
                          <Text size="xs" c="var(--app-text-red)" mt={2}>
                            {problem}
                          </Text>
                        )}
                        {/* QA7 P14: rata przed odbiorem bez własnego dokumentu — skąd dokument i kiedy faktura zaliczkowa */}
                        {k.kind === 'umowa' && documentRequired === 'brak' && trigger !== 'odbior' && (
                          <Text size="xs" c="dimmed" mt={2}>
                            Bez dokumentu: rozliczy ją faktura końcowa. Jeśli wpłata przyjdzie przed odbiorem, wystaw fakturę zaliczkową.
                          </Text>
                        )}
                      </Box>
                    );
                  })}
                  {/* QA8 P16: „po zakończeniu prac” i „po odbiorze” — przy montażu kończonym protokołem ten sam dzień, jedna faktura końcowa */}
                  {(() => {
                    const warn = sameDayInstallmentsWarning(schedule.map((s) => ({ name: s.name, trigger: s.trigger ?? settlementDefaults(s, k.settlementSettings ?? {}).trigger })));
                    return warn ? (
                      <Text size="xs" c="orange.8" mt={4}>
                        {warn}
                      </Text>
                    ) : null;
                  })()}
                  {schedule.length > 0 && (
                    <Text size="xs" c="dimmed" mt={4}>
                      Bez daty w umowie termin płatności ustala się po zdarzeniu: data zdarzenia + „Dni od zdarzenia” pozycji (wpisane w treści umowy), a bez nich — liczba dni z ustawień firmy (domyślnie 7).
                    </Text>
                  )}
                  <Group justify="space-between" mt="sm">
                    <Group>
                      <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={() => setSchedule([...schedule, { key: nk(), name: 'Płatność', amountGr: 0, dueDate: null, conditionsStart: false, payerLabel: '', eventDays: null }])}>
                        Dodaj płatność
                      </Button>
                      {k.kind === 'umowa' && schedule.length > 0 && scheduleSum !== totals.grossGr && (
                        <Button
                          size="xs"
                          variant="subtle"
                          onClick={() => {
                            const others = schedule.slice(0, -1).reduce((a, s) => a + s.amountGr, 0);
                            setSchedule(schedule.map((s, i) => (i === schedule.length - 1 ? { ...s, amountGr: totals.grossGr - others } : s)));
                          }}
                        >
                          Wyrównaj ostatnią płatność
                        </Button>
                      )}
                    </Group>
                    {k.kind === 'umowa' && (
                      <Text size="sm" c={scheduleSum === totals.grossGr ? 'var(--app-text-eko)' : 'var(--app-text-red)'}>
                        Suma harmonogramu <Money gr={scheduleSum} /> {scheduleSum === totals.grossGr ? '= wartość umowy' : `≠ wartość umowy (${formatMoney(totals.grossGr)}) · pozostało ${formatMoney(totals.grossGr - scheduleSum)}`}
                      </Text>
                    )}
                  </Group>
                  {k.kind === 'aneks' && (
                    <Checkbox
                      mt="md"
                      label="Pozycje aneksu to dodatkowe prace"
                      description={
                        annexWorks
                          ? 'Po podpisie aneksu prace wracają do planowania (zadanie dla koordynatora), a płatności aneksu „po odbiorze” czekają na protokół z tych prac. Sprawa nie zamknie się przed ich odbiorem.'
                          : 'Aneks zmienia tylko ceny lub warunki — bez planowania i odbioru dodatkowych prac.'
                      }
                      checked={annexWorks}
                      onChange={(e) => setAnnexWorks(e.currentTarget.checked)}
                    />
                  )}
                  {k.kind === 'aneks' && (
                    <Stack gap={4} mt="md">
                      <Text fw={600} size="sm">
                        Anuluj płatności z harmonogramu umowy {k.parentNumber} (tylko nieopłacone)
                      </Text>
                      {parentCancelable.map((p) => (
                        <Checkbox
                          key={p.id}
                          label={`${p.name} — ${formatMoney(p.amountGr)}`}
                          checked={cancelIds.includes(p.id)}
                          onChange={(e) => {
            const on = e.currentTarget.checked;
            setCancelIds((c) => (on ? [...c, p.id] : c.filter((x) => x !== p.id)));
          }}
                        />
                      ))}
                      {!parentCancelable.length && <EmptyState compact title="Brak nieopłaconych płatności" />}
                    </Stack>
                  )}
                </SectionCard>
              ) : (
                <Stack>
                  <ScheduleTable
                    rows={k.schedule}
                    onSettle={k.can.edit || can(me, 'faktury.wystawianie') || (k.status !== 'roboczy' && k.can.cancel) ? (p) => setSettling(p) : undefined}
                    docCell={showInvoices ? (p) => <ScheduleDocCell contractId={k.id} paymentItemId={p.id} /> : undefined}
                    canPay={k.can.registerPayment}
                    onPay={(p) => {
                      setPay({ ...pay, item: p, amountGr: p.amountGr - p.paidGr });
                      setDialog('pay');
                    }}
                  />
                  {k.can.registerPayment && (
                    <Group>
                      <Button size="xs" variant="light" leftSection={<IconCash size={14} />} onClick={() => {
                        setPay({ ...pay, item: null, amountGr: null });
                        setDialog('pay');
                      }}>
                        Zarejestruj wpłatę
                      </Button>
                    </Group>
                  )}
                  {k.payments.length > 0 && (
                    <SectionCard title="Wpłaty" padding={0}>
                      <Table.ScrollContainer minWidth={620}>
                      <Table highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Data</Table.Th>
                            <Table.Th ta="right">Kwota</Table.Th>
                            <Table.Th>Sposób / pozycja</Table.Th>
                            {showInvoices && <Table.Th>Faktura</Table.Th>}
                            <Table.Th />
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {k.payments.map((p) => (
                            <Table.Tr key={p.id} style={{ opacity: p.reversedBy ? 0.5 : 1 }}>
                              <Table.Td>{datePl(p.paidAt)}</Table.Td>
                              <Table.Td ta="right">
                                <Text c={p.amountGr < 0 ? 'var(--app-text-red)' : undefined} size="sm">
                                  <Money gr={p.amountGr} />
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">
                                  {PAYMENT_METHOD_LABELS[p.method]} {p.reference && `· ${p.reference}`}
                                </Text>
                                <Text size="xs" c="dimmed">
                                  {k.schedule.find((s) => s.id === p.paymentItemId)?.name ?? 'bez przypisania'} · {p.recordedByName ?? 'automatycznie'}
                                  {p.note && ` · ${p.note}`}
                                </Text>
                              </Table.Td>
                              {showInvoices && (
                                <Table.Td>{!p.reversalOf && !p.reversedBy && p.amountGr > 0 && <PaymentInvoiceCell contractId={k.id} paymentId={p.id} />}</Table.Td>
                              )}
                              <Table.Td>
                                {k.can.reversePayment && !p.reversalOf && !p.reversedBy && (
                                  <Button size="compact-xs" variant="subtle" color="red" onClick={() => {
                                    setReverseOf(p);
                                    setDialog('reverse');
                                  }}>
                                    Storno
                                  </Button>
                                )}
                                {p.reversedBy && (
                                  <Badge size="xs" color="dark" variant="light">
                                    wystornowana
                                  </Badge>
                                )}
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                      </Table.ScrollContainer>
                    </SectionCard>
                  )}
                </Stack>
              )}
            </Tabs.Panel>

            {showInvoices && (
              <Tabs.Panel value="faktury">
                <InvoicesPanel contractId={k.id} canConfigure={can(me, 'integracje.konfiguracja')} />
              </Tabs.Panel>
            )}

            <Tabs.Panel value="aneksy">
              {k.annexes.map((a) => (
                <Group key={a.id} py={4}>
                  <Anchor component={Link} to={`/umowy/${a.id}`}>
                    {a.number ?? 'aneks roboczy'}
                  </Anchor>
                  <ContractStatusBadge status={a.status} />
                  {a.signedAt && <Text size="sm">podpisany {datePl(a.signedAt)}</Text>}
                </Group>
              ))}
              {!k.annexes.length && (
                <Card padding={0}>
                  <EmptyState compact title="Brak aneksów" hint={k.can.annex ? 'Nowy aneks utworzysz z menu ⋯ w nagłówku umowy.' : undefined} />
                </Card>
              )}
            </Tabs.Panel>
          </Tabs>

          <Modal opened={dialog === 'esign'} onClose={() => setDialog(null)} title="Wydanie i wysyłka do podpisu elektronicznego" size="lg">
            {dialog === 'esign' && <SendDialog k={k} onClose={() => setDialog(null)} />}
          </Modal>

          <Modal opened={dialog === 'preview'} onClose={() => setDialog(null)} title="Podgląd treści" size="xl">
            {preview?.missing.length ? <Alert color="red" mb="sm">Nieznane pola: {preview.missing.join(', ')}</Alert> : null}
            {preview && <DocumentPreview html={preview.html} />}
          </Modal>

          <Modal opened={dialog === 'sign'} onClose={() => setDialog(null)} title="Odnotowanie podpisu">
            <Stack>
              <TextInput label="Data podpisania" type="date" value={sign.signedAt} onChange={(e) => setSign({ ...sign, signedAt: e.currentTarget.value })} />
              <TextInput label="Kto podpisał" required placeholder="np. Marek Zieliński (Zamawiający), Karolina Wójcik (Wykonawca)" value={sign.signedBy} onChange={(e) => setSign({ ...sign, signedBy: e.currentTarget.value })} />
              <TextInput label="Uwagi" value={sign.note} onChange={(e) => setSign({ ...sign, note: e.currentTarget.value })} />
              <Group>
                <FileButton onChange={(f) => setSign({ ...sign, file: f })} accept="application/pdf,image/jpeg,image/png">
                  {(props) => (
                    <Button {...props} variant="light">
                      Wybierz skan (PDF / zdjęcie)
                    </Button>
                  )}
                </FileButton>
                <Text size="sm">{sign.file?.name ?? 'brak pliku'}</Text>
              </Group>
              <Text size="xs" c="dimmed">
                Po zapisaniu umowa jest niezmienna. Skan jest przechowywany obok wydanego PDF.
              </Text>
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setDialog(null)}>
                  Anuluj
                </Button>
                <Button onClick={submitSign} loading={run.isPending} disabled={!sign.file || sign.signedBy.trim().length < 3}>
                  Zapisz podpis
                </Button>
              </Group>
            </Stack>
          </Modal>

          {settling && <SettlementModal p={settling} onClose={() => setSettling(null)} onSaved={() => void qc.invalidateQueries({ queryKey: ['contract', id] })} />}
          <Modal opened={dialog === 'changes'} onClose={() => setDialog(null)} title="Klient zgłosił zmiany przed podpisem">
            <Stack>
              <Text size="sm" c="dimmed">
                Wydanej umowy nie zmienia się — ta wersja zostanie zastąpiona nową wersją roboczą (kopia treści, pozycji i harmonogramu). Trwający podpis elektroniczny zostanie przerwany. Obie wersje zostają w historii.
              </Text>
              <Textarea label="Co klient chce zmienić" required autosize minRows={3} value={changesNote} onChange={(e) => setChangesNote(e.currentTarget.value)} data-autofocus />
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setDialog(null)}>
                  Anuluj
                </Button>
                <Button
                  disabled={changesNote.trim().length < 3}
                  loading={run.isPending}
                  onClick={() =>
                    run.mutate(async () => {
                      const n = await api<ContractDto>(`/contracts/${id}/changes`, { method: 'POST', body: { note: changesNote } });
                      setDialog(null);
                      setChangesNote('');
                      notifyOk('Utworzono nową wersję roboczą');
                      navigate(`/umowy/${n.id}`);
                    })
                  }
                >
                  Utwórz nową wersję
                </Button>
              </Group>
            </Stack>
          </Modal>
          <Modal opened={dialog === 'toContract'} onClose={() => setDialog(null)} title="Klient chce umowę">
            <Stack>
              <Text size="sm">
                Zlecenie {k.number} zostanie anulowane, a zakresy przejdą zwykłą ścieżką: przygotowanie umowy (specjalista ds. umów), podpis, dalej realizacja. Możliwe tylko
                przed wpłatami, fakturami i rozpoczęciem prac.
              </Text>
              <TextInput label="Powód" placeholder="np. klient prosi o umowę na piśmie" value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setDialog(null)}>
                  Wróć
                </Button>
                <Button
                  loading={run.isPending}
                  onClick={() =>
                    run.mutate(async () => {
                      await api(`/contracts/${id}/to-contract`, { method: 'POST', body: { reason } });
                      notifyOk('Zlecenie anulowane — przygotuj umowę');
                      navigate(`/sprawy/${k.caseId}?zakladka=umowy`);
                    })
                  }
                >
                  Zamień na umowę
                </Button>
              </Group>
            </Stack>
          </Modal>

          <Modal opened={dialog === 'cancel'} onClose={() => setDialog(null)} title={cancelTitle}>
            <Stack>
              <Text size="sm">
                {k.status === 'podpisany'
                  ? 'Anulowanie podpisanej umowy odnotowuje jej rozwiązanie (np. za porozumieniem). Skutki prawne i rozliczenia wpłat pozostają poza systemem.'
                  : k.status === 'wydany'
                    ? 'Dokument wydany do podpisu zostanie anulowany (pozostanie w rejestrze z numerem).'
                    : 'Dokument roboczy zostanie usunięty.'}
              </Text>
              {uncorrected.length > 0 && (
                <Alert color="orange" title="Do tej umowy są wystawione faktury">
                  {uncorrected.map((i) => i.number ?? INVOICE_KINDS[i.kind]).join(', ')} — anulowanie umowy ich nie usuwa (są w Fakturowni{invQ.data?.connection?.mode === 'produkcja' ? ' i KSeF' : ''}). Po anulowaniu wystaw korekty w zakładce „Faktury”.
                </Alert>
              )}
              {k.status === 'roboczy' ? (
                <TextInput label="Powód (opcjonalnie)" value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
              ) : (
                <TextInput label="Powód" description={minHint(reason)} required value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
              )}
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setDialog(null)}>
                  Wróć
                </Button>
                <Button
                  color="red"
                  disabled={k.status !== 'roboczy' && reason.trim().length < 3}
                  loading={run.isPending}
                  onClick={() =>
                    run.mutate(async () => {
                      const r = await api<{ deleted: boolean }>(`/contracts/${id}/cancel`, { method: 'POST', body: { reason } });
                      notifyOk(r.deleted ? (isAnnex ? 'Usunięto aneks roboczy' : 'Usunięto umowę roboczą') : isAnnex ? 'Anulowano aneks' : 'Anulowano umowę');
                      if (r.deleted) navigate(`/sprawy/${k.caseId}?zakladka=umowy`);
                    })
                  }
                >
                  {cancelLabel}
                </Button>
              </Group>
            </Stack>
          </Modal>

          <PaymentDialog
            opened={dialog === 'pay'}
            onClose={() => setDialog(null)}
            contractId={k.id}
            items={k.schedule}
            initialItemId={pay.item?.id ?? null}
            onSaved={() => {
              refresh();
              setDialog(null);
            }}
          />

          <Modal opened={dialog === 'reverse'} onClose={() => setDialog(null)} title="Storno wpłaty">
            <Stack>
              <Text size="sm">
                Wpłata {reverseOf && formatMoney(reverseOf.amountGr)} z {reverseOf ? datePl(reverseOf.paidAt) : ''} pozostanie w historii; storno doda zapis z kwotą ujemną.
              </Text>
              {reverseInvoice && reverseInvoice.status === 'wystawiona' && (
                <Alert color="orange" title={`Do tej wpłaty wystawiono fakturę ${reverseInvoice.number ?? ''}`}>
                  Storno w CRM nie zmienia faktury w Fakturowni. Jeśli pieniądze wróciły do klienta (albo wpłata była błędna), wystaw korektę tej faktury w zakładce „Faktury”.
                </Alert>
              )}
              <TextInput label="Powód" description={minHint(reason)} required value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setDialog(null)}>
                  Anuluj
                </Button>
                <Button
                  color="red"
                  disabled={reason.trim().length < 3}
                  loading={run.isPending}
                  onClick={() =>
                    run.mutate(async () => {
                      const r = await api(`/payments/${reverseOf!.id}/reverse`, { method: 'POST', body: { reason } });
                      notifyOk('Zapisano storno wpłaty');
                      return r;
                    })
                  }
                >
                  Stornuj wpłatę
                </Button>
              </Group>
            </Stack>
          </Modal>
        </div>
      )}
    </QueryState>
  );
}
